import React, { useState, useEffect, useCallback } from 'react';
import {
  C, S, Badge, StatusBadge, FormField, ErrList, StatCard,
  PageHeader, FilterBar, DataTable, Modal, FormGrid,
  useToast, useConfirm, useCRUD,
} from '../../components/inventoryShared';
import ActionBtns from './ActionBtns';
import FormModal from './FormModal';
import {
  categoryController, subCategoryController, brandController,
  unitController, productController, warehouseController,
  locationController, supplierController, purchaseOrderController,
  purchaseReturnController, stockInController, stockOutController,
  stockTransferController, stockAdjustmentController,
  stockController, reportController,
} from '../../controllers/inventoryController';
import {
  Tag, Layers, Boxes, Settings, Package, BarChart3,
  PackageCheck, TrendingDown, ArrowLeftRight, ClipboardList,
  Users, ShoppingCart, RotateCcw, Warehouse, MapPin,
  AlertTriangle, Receipt, FileText, QrCode, Plus,
  Edit2, Trash2, Eye, Download, RefreshCw, CheckCircle,
  XCircle, Truck, ChevronDown, ChevronUp, Search,
} from 'lucide-react';
// ─────────────────────────────────────────────────────────────────────────────
// 7. STORAGE LOCATIONS PAGE
// ─────────────────────────────────────────────────────────────────────────────
export const LocationsPage = ({ companyId }) => {
  const crud = useCRUD(locationController, companyId);
  const [showForm, setShowForm]     = useState(false);
  const [editItem, setEditItem]     = useState(null);
  const [saving, setSaving]         = useState(false);
  const [warehouseList, setWareList]= useState([]);
  const [errors, setErrors]         = useState([]);
  const emptyForm = { name:'',code:'',warehouseId:'',warehouseName:'',zone:'',aisle:'',rack:'',shelf:'',bin:'',type:'Shelf',capacity:0,status:'Active' };
  const [form, setForm] = useState(emptyForm);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  useEffect(() => { warehouseController.getList(companyId).then(r=>r.success&&setWareList(r.data)); }, [companyId]);
  const openAdd  = () => { setEditItem(null); setForm(emptyForm); setErrors([]); setShowForm(true); };
  const openEdit = (r) => { setEditItem(r); setForm({...r}); setErrors([]); setShowForm(true); };
  const handleSave = async () => {
    setSaving(true); setErrors([]);
    const ware = warehouseList.find(w=>w.id===form.warehouseId||w._id===form.warehouseId);
    const payload = { ...form, warehouseName: ware?.name||form.warehouseName, companyId };
    const res = editItem ? await locationController.update(editItem.id,payload) : await locationController.create(payload);
    setSaving(false);
    if (res.success) { crud.show(res.message); setShowForm(false); crud.fetchAll(); }
    else setErrors(res.errors?.length?res.errors:[res.message]);
  };

  const columns = [
    { label:'Code', key:'code', render: v => <span style={{ fontFamily:'monospace', fontSize:12, color:C.teal }}>{v||'—'}</span> },
    { label:'Location', key:'name', render: v => <div style={{ fontWeight:700 }}>{v}</div> },
    { label:'Warehouse', key:'warehouseName', render: v => v||'—' },
    { label:'Type', key:'type', render: v => <Badge color={C.teal} bg={`${C.teal}15`}>{v}</Badge> },
    { label:'Path', key:'zone', render:(_,r) => <span style={{ fontFamily:'monospace', fontSize:12 }}>{[r.zone,r.aisle,r.rack,r.shelf,r.bin].filter(Boolean).join('-')||'—'}</span> },
    { label:'Capacity', key:'capacity', render: v => v||'—' },
    { label:'Status', key:'status', render: v => <StatusBadge status={v}/> },
    { label:'', key:'actions', render:(_, r) => <ActionBtns onEdit={()=>openEdit(r)} onDelete={()=>crud.handleDelete(r.id,`"${r.name}"`)} /> },
  ];

  return (
    <div style={S.page}>
      {crud.ToastEl}{crud.Dialog}
      <PageHeader title="Storage Locations" subtitle="Manage bin/rack locations" Icon={MapPin} color={C.teal}
        action={<button style={S.btnPri} onClick={openAdd}><Plus size={15}/> Add Location</button>} />
      <div style={S.card}>
        <div style={{ padding:'14px 14px 0' }}>
          <FilterBar search={crud.search} onSearch={crud.setSearch}
            filters={[{ key:'warehouseId', value:crud.filters.warehouseId||'', onChange:v=>crud.setFilters(p=>({...p,warehouseId:v})), options:[{value:'',label:'All Warehouses'},...warehouseList.map(w=>({value:w.id||w._id,label:w.name}))] }]} />
        </div>
        <DataTable columns={columns} data={crud.data} loading={crud.loading} emptyTitle="No locations found"
          page={crud.page} totalPages={crud.totalPages} onPageChange={crud.setPage} total={crud.total} />
      </div>
      {showForm && (
        <FormModal title={editItem?'Edit Location':'Add Location'} onClose={()=>setShowForm(false)} onSave={handleSave} saving={saving} maxWidth={680}>
          <ErrList errors={errors} />
          <FormGrid>
            <FormField label="Location Name *"><input style={S.input} value={form.name} onChange={e=>set('name',e.target.value)} /></FormField>
            <FormField label="Code"><input style={S.input} value={form.code} onChange={e=>set('code',e.target.value)} /></FormField>
            <FormField label="Warehouse *">
              <select style={S.select} value={form.warehouseId} onChange={e=>set('warehouseId',e.target.value)}>
                <option value="">Select Warehouse</option>
                {warehouseList.map(w=><option key={w.id||w._id} value={w.id||w._id}>{w.name}</option>)}
              </select>
            </FormField>
            <FormField label="Type"><select style={S.select} value={form.type} onChange={e=>set('type',e.target.value)}>{['Zone','Aisle','Rack','Shelf','Bin','Floor'].map(t=><option key={t}>{t}</option>)}</select></FormField>
            <FormField label="Zone"><input style={S.input} value={form.zone} onChange={e=>set('zone',e.target.value)} placeholder="e.g. A" /></FormField>
            <FormField label="Aisle"><input style={S.input} value={form.aisle} onChange={e=>set('aisle',e.target.value)} placeholder="e.g. 01" /></FormField>
            <FormField label="Rack"><input style={S.input} value={form.rack} onChange={e=>set('rack',e.target.value)} placeholder="e.g. R01" /></FormField>
            <FormField label="Shelf"><input style={S.input} value={form.shelf} onChange={e=>set('shelf',e.target.value)} placeholder="e.g. S01" /></FormField>
            <FormField label="Bin"><input style={S.input} value={form.bin} onChange={e=>set('bin',e.target.value)} placeholder="e.g. B01" /></FormField>
            <FormField label="Capacity"><input type="number" style={S.input} value={form.capacity} onChange={e=>set('capacity',e.target.value)} /></FormField>
            <FormField label="Status"><select style={S.select} value={form.status} onChange={e=>set('status',e.target.value)}><option>Active</option><option>Inactive</option></select></FormField>
          </FormGrid>
        </FormModal>
      )}
    </div>
  );
};
export default LocationsPage;